<template>

    <label for="userPicker"><b>{{label}}</b></label>
    <Button
        id="userPicker"
        class="mt-0"
        label="Change user selected"
        @click="showDialog = true"
        :badge="props.notified_to.length.toString()"
        rounded
    />

    <Dialog v-model:visible="showDialog" header="Select Users" :style="{ width: '60rem' }" @hide="showDialog = false" modal>

        <div class="grid" v-if="!loadingSelectedUsers">

            <div class="col-12">
                <Chip
                    v-for="user in selectedUsers"
                    :key="user.id"
                    :label="user.name + ' ' + user.surname"
                    removable
                    @remove="removeUser(user)"
                    class="mr-2 mb-1"
                />
            </div>
            <!-- Show remove all button once there are more than 1 user selected -->

            <div class="col-6 flex justify-content-start">
                <Button
                    label="Confirm"
                    severity="success"
                    icon="pi pi-check"
                    @click="showDialog = false"
                />
            </div>
            <div class="col-6 flex justify-content-end">
                <Button
                    v-if="selectedUsers.length > 1"
                    label="Remove all selected"
                    severity="danger"
                    icon="pi pi-user-minus"
                    @click="removeAll"
                />
            </div>
        </div>
        <div v-else>
            <ProgressSpinner style="width: 25px; height: 25px" strokeWidth="8" fill="transparent"
                             animationDuration=".5s" aria-label="Custom ProgressSpinner" />
            Loading Users...
        </div>

        <Accordion v-model:activeIndex="activePanel">
            <AccordionTab header="Users filters">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="filters.name" placeholder="Enter User name" />
                    </div>
                    <div class="field col-12 md:col-6">
                        <label for="surname">Surname</label>
                        <InputText id="surname" v-model.trim="filters.surname" placeholder="Enter User surname" />
                    </div>
                    <div class="field col-12 md:col-6">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="filters.email" placeholder="Enter User surname" />
                    </div>
                    <div class="col-12 md:col-6 align-content-center justify-content-start">

                            <Button
                                type="button"
                                label="Search"
                                icon="pi pi-search"
                                :loading="loading"
                                severity="info"
                                @click="filterUsers"
                                class="mr-2"
                            />
                            <Button
                                type="button"
                                label="Clear"
                                icon="pi pi-trash"
                                severity="warning"
                                :disabled="filters.name.length === 0 && filters.surname.length === 0 && filters.email.length === 0 "
                                @click="resetFilters"
                            />
                    </div>
                </div>
            </AccordionTab>
            <AccordionTab header="Users list">
                <DataTable
                    :value="userList"
                    :lazy="true"
                    :paginator="true"
                    :rows="rows"
                    ref="dt"
                    dataKey="id"
                    :totalRecords="totalRecords"
                    :loading="loading"
                    @page="onPage($event)"
                    class="mt-2"
                    responsiveLayout="scroll"
                    paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
                >
                    <template #empty> No Groups Available. </template>

                    <Column field="id" header="Id" >
                        <template #body="{ data }">
                            <Button
                                :disabled="selectedUsers.some(item => item.id === data.id)"
                                severity="success" rounded
                                icon="pi pi-user-plus"
                                @click="selectUser(data)"
                                :loading="loadingSelectedUsers"
                            />
                        </template>
                    </Column>
                    <Column field="name" header="Name"/>
                    <Column field="surname" header="Surname"/>
                    <Column field="email" header="email"/>
                </DataTable>
            </AccordionTab>
        </Accordion>
    </Dialog>

</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, watch } from 'vue';
import { authApi } from '@/utilities/axios';
import { authStore } from '@/store/global-stores';

const emit = defineEmits(['selectedUsers']);

const showDialog = ref(false)
const loading = ref(false)
const userList = ref([])
const totalRecords = ref(0)
const currentPage = ref(1)
const selectedUsers = ref([])
const rows = ref(5)
const activePanel = ref(1);

const filters = ref({
    name: "",
    surname: "",
    email: "",
})

const loadingSelectedUsers = ref(false)

const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

const props = defineProps({
    loading: {
        type: Boolean,
        default: false,
    },
    value: {
        type: String,
        default: "id",
    },
    description: {
        type: String,
        default: "name",
    },
    label: {
        type: String,
        default: "Users:",
    },
    notified_to: {
        type: Array,
    },
});

onMounted(() => {
    emit('hide-dialog')

    loadSavedUsers(props.notified_to)
    loadAllUsers()

    filters.value.surname = filters.value.name = filters.value.email = ''
});

function onPage(event) {
    currentPage.value = event.page + 1
    loadAllUsers()
}

watch(props.notified_to, () => {
    userList.value = []
    selectedUsers.value = []
    loadSavedUsers(props.notified_to)

    filters.value.surname = filters.value.name = filters.value.email = ''
});

function loadSavedUsers(ids, page) {

    let params = {
        company_id: company_id.value,
        page_size: rows.value,
        ids: ids,
    }

    if (typeof page === 'undefined') {
        page = 1
        selectedUsers.value = []
        loadingSelectedUsers.value = true
    }

    params.page = page

    if (typeof ids !== 'undefined' && ids.length > 0) {
        authApi.get('/v2/admin/users', {
            params: params,
        }).then((response) => {

            if (response.statusCode === 200 && Object.hasOwn(response, 'data')) {

                response.data.data.forEach((user) => {
                    selectedUsers.value.push(user)
                })

                if (selectedUsers.value.length < response.data.total) {
                    loadSavedUsers(ids, page+1)
                } else {
                    loadingSelectedUsers.value = false
                }
            }
        })
    } else {
        loadingSelectedUsers.value = false
    }
}

function resetFilters() {
    filters.value.name = filters.value.surname = filters.value.email = ''
    loadAllUsers()
    activePanel.value = 1
}

function filterUsers() {
    loadAllUsers()
    activePanel.value = 1
}

function loadAllUsers() {
    loading.value = true
    userList.value = []

    let params = {
        company_id: company_id.value,
        page_size: rows.value,
        page: currentPage.value
    }

    if (filters.value.name.length > 0) {
        params.name = filters.value.name
    }

    if (filters.value.surname.length > 0) {
        params.surname = filters.value.surname
    }

    if (filters.value.email.length > 0) {
        params.email = filters.value.email
    }

    authApi.get('/v2/admin/users', {
        params: params,
    }).then((response) => {

        if (response.statusCode === 200 && Object.hasOwn(response, 'data')) {
            userList.value = response.data.data
            loading.value = false

            totalRecords.value = response.data.total
        }
    })
}

function selectUser(user) {
    selectedUsers.value.push(user)
    emit('selectedUsers', selectedUsers.value)
}

function removeUser(user) {
    selectedUsers.value = selectedUsers.value.filter(function (item) {
        return item.id !== user.id;
    });

    emit('selectedUsers', selectedUsers.value)
}

function removeAll() {
    selectedUsers.value = []
    emit('selectedUsers', selectedUsers.value)
}

</script>

<style scoped>

.field {
    margin: 0.75rem 0;
    font-size: 1rem;
}
.field > label {
    display: block;
}
.field > span {
    /* display: block; */
    opacity: 0.7;
    margin-left: 4px;
}
.field span.p-tag {
    display: inline-block;
    opacity: 1;
}
</style>